import React, { useState, useRef, useEffect, useCallback } from 'react';
import { NavLink, Outlet, useNavigate, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { isExternalRole } from './externalRoles';
import { isTenantAdminUser } from './RequireTenantAdmin';
import { useOwnerMode, OWNER_MODES } from './OwnerModeContext';
import WorkspaceSwitcher from './components/WorkspaceSwitcher';
import { api } from './api';
import { addressLine } from './shared/address';
import { formatPhone } from './shared/phone';
import { getImpersonation, impersonationMatches, exitImpersonation as exitImpersonationState, clearImpersonation } from './impersonation';
import { applyBranding, applyChromeColors, brandAssetUrl, setTitleUnread, setTitlePage } from '../branding';
import OnboardingTour from './OnboardingTour';
import TopNavPanel, { MenuBackdrop, pressedOutside } from './components/TopNavPanel';
import { useLang } from './LanguageContext';
import {
  Search, Plus, Sparkles, Inbox as InboxIcon,
  LogOut, ChevronDown, Menu, X, Shield, Building2, Home, MessageSquare, UserCog, Bell,
} from 'lucide-react';
import NotificationBell from './NotificationBell';
import TasksMenu from './TasksMenu';
import { LiveInboxProvider, useLiveInbox } from './LiveInboxContext';
import FeedbackDialog from './FeedbackDialog';
import SpendApprovalBanner from './SpendApprovalBanner';
import AppErrorBoundary from '../components/AppErrorBoundary';
import { setSentryUser } from '../observability';

// Arive-style horizontal nav items. Each item has an optional `products` array —
// if set, only show for tenants licensed for at least one of them (`hasProduct`).
// 'mortgage' → LOS + compliance. 'real_estate' → CRM + deals only. 'dual_licensed' → both.
const NAV = [
  { to: '/portal', label: 'Dashboard', tk: 'nav.dashboard', end: true },
  { to: '/portal/leads', label: 'Leads', tk: 'nav.leads' },
  { to: '/portal/pipeline', label: 'Pipeline', tk: 'nav.pipeline' },
  { to: '/portal/contacts', label: 'Contacts', tk: 'nav.contacts' },
  { to: '/portal/calendar', label: 'Calendar', tk: 'nav.calendar' },
  { to: '/portal/loans', label: 'Loans', tk: 'nav.loans', products: ['mortgage'] },
  { to: '/portal/deals', label: 'Deals', tk: 'nav.deals' },
  { to: '/portal/real-estate', label: 'Real Estate', tk: 'nav.realestate', products: ['real_estate'] },
  { to: '/portal/compliance', label: 'Reports', tk: 'nav.reports', products: ['mortgage'] },
];

const OWNER_MODE_LABELS = {
  auto: 'Auto (Tenant)', mortgage: 'Mortgage', real_estate: 'Real Estate', dual_licensed: 'Dual Licensed',
};
// Phone labels — the full ones wrap the owner bar onto two rows below `sm`.
const OWNER_MODE_SHORT = {
  auto: 'Auto', mortgage: 'Mortgage', real_estate: 'RE', dual_licensed: 'Dual',
};

// Secondary utilities accessed from a "More" menu — tools only (ticket 107).
// Grouped under a small header per `group`; a group with no visible items
// (product-gated out) renders no header.
// Deliberately URL-only (Ken, 2026-08-26: "later"): /portal/partners, /portal/refer.
const MORE = [
  { to: '/portal/compliance/kit', label: 'Compliance Kit', tk: 'nav.compliancekit', products: ['mortgage', 'real_estate'], group: 'Tools' },
  { to: '/portal/fee-worksheet', label: 'Fee Worksheet', tk: 'nav.feeworksheet', products: ['mortgage'], group: 'Tools' },
  { to: '/portal/mcr', label: 'Mortgage Call Reports', tk: 'nav.mcr', products: ['mortgage'], group: 'Tools' },
  { to: '/portal/calculators', label: 'Calculators', tk: 'nav.calc', products: ['mortgage'], group: 'Tools' },
  { to: '/portal/income', label: 'Income Calculator', tk: 'nav.income', products: ['mortgage'], group: 'Tools' },
  { to: '/portal/documents', label: 'Documents', tk: 'nav.documents', group: 'Marketing & Ops' },
  { to: '/portal/marketing', label: 'Marketing Studio', tk: 'nav.marketingstudio', group: 'Marketing & Ops' },
  { to: '/portal/email-analytics', label: 'Email Analytics', tk: 'nav.emailanalytics', group: 'Marketing & Ops' },
  { to: '/portal/commissions', label: 'Commissions', tk: 'nav.commissions', group: 'Marketing & Ops' },
  { to: '/portal/automations', label: 'Automations', tk: 'nav.automations', group: 'Marketing & Ops' },
];
const MORE_GROUPS = ['Tools', 'Marketing & Ops'];
const GROUP_KEYS = { Tools: 'nav.group.tools', 'Marketing & Ops': 'nav.group.marketingops' };

// Global search waits this long after the last keystroke before it asks the server.
const SEARCH_DEBOUNCE_MS = 250;

// Organization-level settings — live under the avatar menu (ticket 107), gated
// the same way the old `MORE` `adminOnly` entries were: tenant owners/admins
// and the platform super-admin (ticket 37).
const ADMIN_LINKS = [
  { to: '/portal/settings/org', label: 'Organization Settings', tk: 'nav.org', testId: 'usermenu-organization-settings' },
  { to: '/portal/settings/branding', label: 'Brand & Website', tk: 'nav.brand', testId: 'usermenu-brand-website' },
  { to: '/portal/settings/billing', label: 'Billing', tk: 'nav.billing', testId: 'usermenu-billing' },
  { to: '/portal/enterprise', label: 'Enterprise', tk: 'nav.enterprise', testId: 'usermenu-enterprise' },
];

// Ticket 154: the provider sits above the layout body so the nav badge and
// the Inbox page share one summary poll.
export default function PortalLayout() {
  const { user } = useAuth();
  const enabled = !!user && !isExternalRole(user.role);
  return (
    <LiveInboxProvider enabled={enabled}>
      <PortalLayoutBody />
    </LiveInboxProvider>
  );
}

function PortalLayoutBody() {
  const { user, loading, logout } = useAuth();
  const { unreadContacts } = useLiveInbox();
  // One top-nav menu open at a time: 'drawer' | 'more' | 'add' | 'user' | 'bell' | null.
  const [openMenu, setOpenMenu] = useState(null);
  const toggleMenu = (name) => setOpenMenu((cur) => (cur === name ? null : name));
  const closeMenus = () => setOpenMenu(null);
  const mobileOpen = openMenu === 'drawer';
  const moreMenu = openMenu === 'more';
  const addMenu = openMenu === 'add';
  const userMenu = openMenu === 'user';
  // Falls back to initials when the OAuth provider's avatar URL fails to load.
  const [avatarBroken, setAvatarBroken] = useState(false);
  const [logoBroken, setLogoBroken] = useState(false);
  const [tenant, setTenant] = useState(null);
  // True when GET /tenants/mine answers 404 `no_active_tenant` (ticket 55): the
  // user exists but is attached to no workspace. Render a holding state instead
  // of the Outlet — every scoped route would show nothing anyway.
  const [noWorkspace, setNoWorkspace] = useState(false);
  const [feedbackOpen, setFeedbackOpen] = useState(false);
  const [inbox, setInbox] = useState({ total_unread: 0, mention_count: 0 });
  // Ticket 191: deal-message unread (the Deals nav item) and contacts with
  // unread texts/emails (the Inbox nav item, ticket 154) are separate badges.
  const dealsBadge = inbox.mention_count || inbox.total_unread || 0;
  const inboxBadge = unreadContacts || undefined;
  const [showTour, setShowTour] = useState(false);
  // Global search
  const [searchQ, setSearchQ] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchOpen, setSearchOpen] = useState(false);
  // Row highlighted by the arrow keys (index into the openable rows), -1 = none.
  const [activeIdx, setActiveIdx] = useState(-1);
  // Below `xl` the search pill collapses to an icon button (ticket 107 overflow fix).
  const [searchExpanded, setSearchExpanded] = useState(false);
  const { lang, setLang, t } = useLang();
  const navigate = useNavigate();
  const location = useLocation();
  const searchRef = useRef(null);
  const moreRef = useRef(null);
  const addRef = useRef(null);
  const userRef = useRef(null);
  const headerRef = useRef(null);
  const morePanelRef = useRef(null);
  const addPanelRef = useRef(null);
  const userPanelRef = useRef(null);
  const searchBoxRef = useRef(null);
  const searchPanelRef = useRef(null);

  // Click-outside + Escape close whichever menu is open. mousedown (not blur)
  // so NavLink clicks inside a menu always land. The drawer counts the whole
  // header as "inside" — tapping another header control swaps menus instead.
  // The bell runs its own outside-click and reports back via onOpenChange.
  // Panels are portalled out of the header (TopNavPanel), so each menu's
  // "inside" is its trigger wrapper plus its panel.
  useEffect(() => {
    if (!openMenu) return undefined;
    const inside = {
      drawer: [headerRef],
      more: [moreRef, morePanelRef],
      add: [addRef, addPanelRef],
      user: [userRef, userPanelRef],
    }[openMenu];
    const onDocMouseDown = (e) => {
      if (inside && pressedOutside(e, inside)) setOpenMenu(null);
    };
    const onKey = (e) => { if (e.key === 'Escape') setOpenMenu(null); };
    document.addEventListener('mousedown', onDocMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onDocMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [openMenu]);

  // Any navigation closes every menu.
  useEffect(() => { setOpenMenu(null); }, [location.pathname]);

  // Sentry actor context (ticket 123) — id + tenant_id only, cleared on logout.
  useEffect(() => { setSentryUser(user); }, [user]);

  // Load tenant (drives product-type gating on nav). Also the banner's refresh.
  const loadTenant = useCallback(() => api.get('/tenants/mine')
    .then(({ data }) => { setTenant(data); setNoWorkspace(false); })
    .catch((err) => { if (isNoActiveTenant(err)) setNoWorkspace(true); }), []);
  useEffect(() => {
    if (user) {
      loadTenant();
      // Show first-run walkthrough — but only once per user (backend stores onboarded_at).
      if (!user.onboarded_at) setShowTour(true);
      // Sync persisted language across devices — if user has a saved language and it
      // differs from the currently-active portal language, adopt it.
      if (user.language && user.language !== lang) setLang(user.language);
    }
  }, [user]);

  // Ticket 154 P3: the tab title carries both nav badges added together and
  // names the Inbox route; both reset on unmount.
  const titleUnread = dealsBadge + unreadContacts;
  useEffect(() => {
    setTitleUnread(titleUnread || 0);
    return () => setTitleUnread(0);
  }, [titleUnread]);
  useEffect(() => {
    setTitlePage(location.pathname.startsWith('/portal/inbox') ? 'Inbox' : '');
    return () => setTitlePage('');
  }, [location.pathname]);

  // Tab title + favicon follow the tenant's branding
  useEffect(() => {
    if (tenant) applyBranding({ title: tenant.name, favicon: tenant.favicon_url });
  }, [tenant]);

  // Header logo: the tenant's own, else the BrokerForYou mark (ticket 203). A logo that
  // fails to load falls back too; a new logo gets a fresh try.
  useEffect(() => { setLogoBroken(false); }, [tenant?.logo_url]);
  const tenantLogo = tenant?.logo_url && !logoBroken ? brandAssetUrl(tenant.logo_url) : null;

  // The top bar follows the tenant's colors (ticket 203); cleared on leaving the portal.
  useEffect(() => {
    applyChromeColors(tenant?.colors);
    return () => applyChromeColors(null);
  }, [tenant]);

  // Poll deal-messages inbox summary for the nav badge
  useEffect(() => {
    if (!user) return;
    let stopped = false;
    const fetchInbox = () => {
      api.get('/deals/messages/inbox-summary')
        .then(({ data }) => { if (!stopped) setInbox(data); })
        .catch(() => {});
    };
    fetchInbox();
    const id = setInterval(fetchInbox, 30000);
    return () => { stopped = true; clearInterval(id); };
  }, [user]);

  // Platform owner = the `is_super_admin` flag from /auth/me (ticket 54), not an email match.
  const isOwner = !!user?.is_super_admin;

  // Shared owner-mode context — reactive across every tab.
  const { ownerMode, setOwnerMode: setOwnerModeAndPersist, hasProduct, setTenantProductType } = useOwnerMode();

  // Impersonation banner (ticket 73). Re-read on every route change so
  // entering/exiting login-as never needs a hard reload, and drop a flag that
  // was written for a different user than the one signed in now — that is
  // what an expired impersonation followed by a normal sign-in looks like.
  const [imp, setImp] = useState(getImpersonation);
  useEffect(() => { setImp(getImpersonation()); }, [location.key]);
  useEffect(() => {
    if (imp && user && !impersonationMatches(imp, user)) { clearImpersonation(); setImp(null); }
  }, [imp, user]);
  const exitImpersonation = () => {
    const restored = exitImpersonationState();
    window.location.href = restored ? '/portal/super-admin' : '/portal/login';
  };
  const rawProductType = tenant?.product_type || 'mortgage';
  // Push tenant's raw product type into the context so consumers on other routes
  // (e.g., SuperAdminPage) can compute the same effective productType.
  useEffect(() => { setTenantProductType(rawProductType); }, [rawProductType, setTenantProductType]);

  // Tenant owner/admin (raw membership role; `owner` reads as `admin` on `role`) or platform super-admin.
  const isTenantAdmin = isTenantAdminUser(user);
  const filterNav = (items) => items.filter((n) =>
    (!n.products || n.products.some(hasProduct)) && (!n.adminOnly || isTenantAdmin));
  const visibleNav = filterNav(NAV).map((n) => (n.to === '/portal/deals' ? { ...n, badge: dealsBadge || undefined } : n));
  const visibleMore = filterNav(MORE);
  const visibleAdminLinks = isTenantAdmin ? ADMIN_LINKS : [];

  // cmd-K / "/" focus search — must work at every width, so expand the pill first.
  useEffect(() => {
    const handler = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        setOpenMenu(null);
        setSearchExpanded(true);
        searchRef.current?.focus();
      } else if (e.key === '/' && document.activeElement?.tagName !== 'INPUT' && document.activeElement?.tagName !== 'TEXTAREA') {
        e.preventDefault();
        setSearchExpanded(true);
        searchRef.current?.focus();
      } else if (e.key === 'Escape' && searchExpanded) {
        setSearchExpanded(false);
        searchRef.current?.blur();
      }
    };
    window.addEventListener('keydown', handler);
    return () => window.removeEventListener('keydown', handler);
  }, [searchExpanded]);

  // Debounced search (ticket 196): one request per pause in typing, and a
  // response that arrives after the text moved on is dropped (the cleanup
  // flips `stale` for the request it belongs to).
  useEffect(() => {
    if (searchQ.length < 2) { setSearchResults(null); setSearchOpen(false); return undefined; }
    let stale = false;
    const timer = setTimeout(async () => {
      try {
        const { data } = await api.get('/v2/contacts/search', { params: { q: searchQ, limit: 8 } });
        if (stale) return;
        setSearchResults(data);
        setSearchOpen(true);
        setActiveIdx(-1);
      } catch {
        if (!stale) setSearchResults(null);
      }
    }, SEARCH_DEBOUNCE_MS);
    return () => { stale = true; clearTimeout(timer); };
  }, [searchQ]);

  // Click-outside closes the results and folds the icon-sized pill back up.
  // A press inside the box or on the portalled results panel is "inside", so a
  // click on a result always lands — no blur timer racing it.
  useEffect(() => {
    if (!searchOpen && !searchExpanded) return undefined;
    const onDocMouseDown = (e) => {
      if (pressedOutside(e, [searchBoxRef, searchPanelRef])) { setSearchOpen(false); setSearchExpanded(false); }
    };
    document.addEventListener('mousedown', onDocMouseDown);
    return () => document.removeEventListener('mousedown', onDocMouseDown);
  }, [searchOpen, searchExpanded]);

  // Keep the arrow-key row visible inside the scrolling panel.
  useEffect(() => {
    if (activeIdx >= 0) document.getElementById(`gsr-opt-${activeIdx}`)?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIdx]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ background: '#f8fafc' }}>
        <div className="w-12 h-12 border-4 border-slate-200 border-t-[#6BA82E] rounded-full animate-spin" />
      </div>
    );
  }

  if (!user) return <Navigate to="/portal/login" replace />;
  if (user.role === 'borrower') return <Navigate to="/borrower" replace />;
  if (user.role === 'realtor') return <Navigate to="/realtor" replace />;
  if (user.role === 'party') return <Navigate to="/party" replace />;

  const doLogout = async () => { await logout(); navigate('/portal/login', { replace: true }); };

  // Rows the arrow keys can reach, in the order they are drawn. Properties are
  // not here: the app has no property page, so those rows are plain text.
  const searchRows = searchResults ? [
    ...(searchResults.contacts || []).map((c) => ({ key: `c:${c.contact_id}`, to: `/portal/contacts/${c.contact_id}` })),
    ...(searchResults.opportunities || []).map((o) => ({ key: `o:${o.opportunity_id}`, to: `/portal/pipeline?board=mortgage&open=${o.opportunity_id}` })),
    ...(searchResults.transactions || []).map((t) => ({ key: `t:${t.transaction_id}`, to: `/portal/real-estate/transactions/${t.transaction_id}` })),
    ...(searchResults.loan_files || []).map((l) => ({ key: `l:${l.loan_file_id}`, to: `/portal/loans/${l.loan_file_id}` })),
    ...(searchResults.tasks || []).map((t) => ({ key: `k:${t.task_id}`, to: `/portal/tasks?q=${encodeURIComponent(t.title)}` })),
  ] : [];
  const rowProps = (key) => {
    const idx = searchRows.findIndex((r) => r.key === key);
    return {
      id: `gsr-opt-${idx}`,
      active: idx === activeIdx,
      onGo: () => openSearchRow(searchRows[idx].to),
      onHover: () => setActiveIdx(idx),
    };
  };
  const openSearchRow = (to) => {
    navigate(to);
    setSearchOpen(false);
    setSearchExpanded(false);
    setSearchQ('');
    searchRef.current?.blur();
  };
  const onSearchKeyDown = (e) => {
    if (e.key === 'Escape') { setSearchOpen(false); setActiveIdx(-1); return; }
    // Tabbing away leaves the box the way the old blur did.
    if (e.key === 'Tab') { setSearchOpen(false); setSearchExpanded(false); return; }
    if (!searchOpen || !searchRows.length) return;
    if (e.key === 'ArrowDown') { e.preventDefault(); setActiveIdx((i) => (i + 1) % searchRows.length); }
    else if (e.key === 'ArrowUp') { e.preventDefault(); setActiveIdx((i) => (i <= 0 ? searchRows.length - 1 : i - 1)); }
    else if (e.key === 'Enter' && searchRows[activeIdx]) { e.preventDefault(); openSearchRow(searchRows[activeIdx].to); }
  };

  return (
    <div className="portal-shell min-h-screen flex flex-col" style={{ background: 'linear-gradient(180deg, #F7F8FC 0%, #EEF0F8 100%)' }}>
      {/* Impersonation banner — appears whenever the owner is logged-in AS another user */}
      {imp && (
        <div
          className="w-full text-white text-center text-[13px] font-semibold py-1.5 flex items-center justify-center gap-3 print:hidden"
          style={{ background: 'linear-gradient(90deg, #DC2626 0%, #B91C1C 100%)' }}
          data-testid="impersonation-banner"
        >
          <Shield size={13} />
          <span>🎭 Impersonating <b>{imp.tenant_name}</b> as <b>{imp.as_email}</b> ({imp.as_role})</span>
          <button
            onClick={exitImpersonation}
            data-testid="exit-impersonation"
            className="ml-2 text-[11px] font-bold px-2.5 py-0.5 rounded bg-white/95 hover:bg-white transition"
            style={{ color: '#B91C1C' }}
          >
            EXIT IMPERSONATION
          </button>
        </div>
      )}

      {/* Ticket 167: money-costing features stay off until a super-admin approves the workspace */}
      <div className="print:hidden">
      <SpendApprovalBanner
        tenant={tenant}
        canRequest={isTenantAdmin}
        onContact={() => setFeedbackOpen(true)}
        onRefresh={user ? loadTenant : undefined}
        onRequested={(r) => setTenant((t) => (t ? {
          ...t,
          spend_approval: r.spend_approval || t.spend_approval,
          spend_approval_requested_at: r.spend_approval_requested_at ?? t.spend_approval_requested_at,
        } : t))}
      />
      </div>

      {/* Owner-only product-mode toggle — flips nav between mortgage/RE views */}
      {isOwner && !imp && (
        <div className="w-full py-1 px-2 flex items-center justify-center gap-2 text-[11px] whitespace-nowrap print:hidden" style={{ background: '#0a0d3a', color: '#A6E56C' }} data-testid="owner-mode-toggle">
          <Shield size={10} className="shrink-0" />
          <span className="text-white/70">Owner view:</span>
          <div className="inline-flex rounded overflow-hidden" style={{ border: '1px solid rgba(166,229,108,0.3)' }}>
            {OWNER_MODES.map((m) => (
              <button
                key={m}
                onClick={() => setOwnerModeAndPersist(m)}
                data-testid={`owner-mode-${m}`}
                title={OWNER_MODE_LABELS[m]}
                className="px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider transition"
                style={ownerMode === m
                  ? { background: 'var(--brand-highlight, #A6E56C)', color: 'var(--brand-on-highlight, #030524)' }
                  : { background: 'transparent', color: '#A6E56C' }}
              >
                <span className="sm:hidden">{OWNER_MODE_SHORT[m]}</span>
                <span className="hidden sm:inline">{OWNER_MODE_LABELS[m]}</span>
              </button>
            ))}
          </div>
          {ownerMode !== 'auto' && (
            <span className="hidden sm:inline text-white/60 ml-2">(overriding tenant default: <b>{rawProductType}</b>)</span>
          )}
        </div>
      )}

      {/* Global top nav — TCL brand: layered navy w/ green accent bar */}
      <header
        ref={headerRef}
        className="sticky top-0 z-40 border-b relative overflow-x-clip print:hidden"
        style={{
          background: 'linear-gradient(180deg, var(--brand-primary, #070B5F) 0%, color-mix(in srgb, var(--brand-primary, #070B5F) 92%, white) 100%)',
          borderColor: 'color-mix(in srgb, var(--brand-accent, #6BA82E) 35%, transparent)',
          boxShadow: '0 1px 0 color-mix(in srgb, var(--brand-accent, #6BA82E) 15%, transparent), 0 4px 20px color-mix(in srgb, var(--brand-primary, #070B5F) 15%, transparent)',
        }}
        data-testid="global-topnav"
      >
        {/* Thin green accent stripe */}
        <div className="absolute inset-x-0 bottom-0 h-[2px]" style={{ background: 'linear-gradient(90deg, transparent 0%, var(--brand-accent, #6BA82E) 50%, transparent 100%)', opacity: 0.6 }} />
        <div className="flex items-center h-14 px-3 md:px-5 gap-2 relative min-w-0">
          {/* Logo — md+ only; phones lead with the menu button. */}
          <NavLink to="/portal" className="hidden md:flex items-center gap-2 pr-3 mr-1 shrink-0" data-testid="topnav-logo">
            {tenantLogo ? (
              <img
                src={tenantLogo}
                alt={tenant.name || 'Logo'}
                className="h-10 w-auto max-w-[180px] object-contain"
                data-testid="topnav-logo-img"
                onError={() => setLogoBroken(true)}
              />
            ) : (
              <img src="/brand/brokerforyou-white.png?v=3" alt="BrokerForYou" className="h-10 w-auto" data-testid="topnav-logo-img" />
            )}
          </NavLink>
          <WorkspaceSwitcher />

          {/* Left: primary nav (desktop). Items tighten below xl so the full nav
              fits beside the icon cluster at 1024px. */}
          <nav className="hidden lg:flex items-center gap-0.5 min-w-0 min-[1440px]:shrink-0" data-testid="topnav-primary">
            {visibleNav.map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                data-testid={`topnav-${it.label.toLowerCase().replace(/\s/g,'-')}`}
                className={({ isActive }) =>
                  `px-2 xl:px-2.5 py-1.5 rounded-md text-[12px] xl:text-[12.5px] font-medium transition-colors relative group ${
                    isActive
                      ? 'text-white'
                      : 'text-white/70 hover:text-white hover:bg-white/5'
                  }`
                }
              >
                {({ isActive }) => (
                  <>
                    <span className="relative z-10">{t(it.tk)}</span>
                    {it.badge > 0 && (
                      <span className="relative z-10 ml-1 min-w-[16px] h-[16px] px-1 rounded-full text-[9px] font-bold inline-flex items-center justify-center align-middle" style={{ background: 'var(--brand-accent, #6BA82E)', color: 'var(--brand-on-accent, #030524)' }} data-testid="nav-badge-deals">{it.badge}</span>
                    )}
                    {isActive && (
                      <span className="absolute inset-x-2 -bottom-[13px] h-[2.5px] rounded-t-full" style={{ background: 'var(--brand-accent, #6BA82E)', boxShadow: '0 0 12px color-mix(in srgb, var(--brand-accent, #6BA82E) 60%, transparent)' }} />
                    )}
                  </>
                )}
              </NavLink>
            ))}
            {/* More menu */}
            <div className="relative" ref={moreRef}>
              <button
                type="button"
                onClick={() => toggleMenu('more')}
                data-testid="topnav-more"
                className="px-2 xl:px-3 py-1.5 rounded-md text-[12px] xl:text-[13px] font-medium text-white/70 hover:text-white hover:bg-white/5 inline-flex items-center gap-1"
              >
                {t('nav.more')} <ChevronDown size={12} />
              </button>
              {moreMenu && (
                <TopNavPanel ref={morePanelRef} anchorRef={moreRef} onDismiss={closeMenus} align="left" data-testid="topnav-more-menu" className="w-56 rounded-lg border shadow-2xl py-1.5" style={{ background: 'var(--brand-primary, #070B5F)', borderColor: 'color-mix(in srgb, var(--brand-accent, #6BA82E) 25%, transparent)' }}>
                  {MORE_GROUPS.map((group) => {
                    const items = visibleMore.filter((it) => it.group === group);
                    if (!items.length) return null;
                    return (
                      <div key={group}>
                        <div className="text-[10px] uppercase tracking-wide text-white/40 px-3 pt-2 pb-1">{t(GROUP_KEYS[group])}</div>
                        {items.map((it) => (
                          <NavLink
                            key={it.to}
                            to={it.to}
                            onClick={closeMenus}
                            className="block px-3 py-2 text-[13px] text-white/80 hover:bg-white/5 hover:text-white"
                          >
                            {t(it.tk)}
                          </NavLink>
                        ))}
                      </div>
                    );
                  })}
                </TopNavPanel>
              )}
            </div>
          </nav>

          {/* Mobile menu button */}
          <button className="lg:hidden -ml-1.5 md:ml-0 p-1.5 sm:p-2 text-white/80" aria-label="Menu" aria-expanded={mobileOpen} onClick={() => toggleMenu('drawer')} data-testid="topnav-mobile-toggle">
            {mobileOpen ? <X size={18} /> : <Menu size={18} />}
          </button>

          <div className="flex-1" />

          {/* Search pill (cmd-K style) — pill at 1440px+, collapses to an icon below
              (toggle lives in the right cluster) until blur/Escape (ticket 107).
              Below 1440 the full primary nav needs the room; at xl the pill overlapped
              Reports/More. The pill shrinks before the nav does. */}
          {/* The expanded overlay is opaque navy so the icon cluster beneath doesn't show through. */}
          <div className={`${searchExpanded ? 'flex absolute right-3 top-1/2 -translate-y-1/2 w-72 z-50 bg-[#161A69] shadow-lg' : 'hidden'} items-center rounded-full px-3 h-9 gap-2 min-[1440px]:flex min-[1440px]:relative min-[1440px]:top-auto min-[1440px]:right-auto min-[1440px]:translate-y-0 min-[1440px]:w-[280px] min-[1440px]:min-w-[180px] min-[1440px]:z-auto min-[1440px]:bg-white/[0.06] min-[1440px]:shadow-none`}
               ref={searchBoxRef}
               style={{ border: '1px solid rgba(255,255,255,0.08)' }}>
            <Search size={14} className="text-white/50 shrink-0" />
            <input
              ref={searchRef}
              type="text"
              placeholder="Search contacts, deals, tasks, properties..."
              data-testid="global-search"
              value={searchQ}
              onChange={(e) => setSearchQ(e.target.value)}
              onFocus={() => { if (searchResults) setSearchOpen(true); }}
              onKeyDown={onSearchKeyDown}
              role="combobox"
              aria-expanded={!!(searchOpen && searchResults)}
              aria-controls="global-search-results"
              aria-activedescendant={searchOpen && activeIdx >= 0 ? `gsr-opt-${activeIdx}` : undefined}
              className="bg-transparent text-[13px] text-white placeholder-white/40 outline-none flex-1 min-w-0"
            />
            <kbd className="text-[9px] font-mono px-1.5 py-0.5 rounded text-white/50 border border-white/10 shrink-0">⌘K</kbd>
            {searchOpen && searchResults && (
              <TopNavPanel
                ref={searchPanelRef}
                anchorRef={searchBoxRef}
                onDismiss={() => { setSearchOpen(false); setSearchExpanded(false); }}
                matchWidth
                gap={8}
                maxHeight={420}
                className="rounded-xl shadow-2xl"
                style={{ background: '#0a0f2a', border: '1px solid rgba(255,255,255,0.1)' }}
                id="global-search-results"
                role="listbox"
                data-testid="global-search-results"
              >
                {(searchResults.contacts || []).length === 0 &&
                 (searchResults.properties || []).length === 0 &&
                 (searchResults.opportunities || []).length === 0 &&
                 (searchResults.transactions || []).length === 0 &&
                 (searchResults.loan_files || []).length === 0 &&
                 (searchResults.tasks || []).length === 0 && (
                  <div className="p-4 text-xs text-white/50">No results</div>
                )}
                {(searchResults.contacts || []).length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-white/40">Contacts</div>
                    {searchResults.contacts.map((c) => (
                      <SearchRow key={c.contact_id} {...rowProps(`c:${c.contact_id}`)} testId={`gsr-contact-${c.contact_id}`}>
                        <div className="text-[13px] text-white">{c.display_name}</div>
                        <div className="text-[11px] text-white/50">{c.emails?.[0]?.value || formatPhone(c.phones?.[0]?.value) || c.contact_type}</div>
                      </SearchRow>
                    ))}
                  </div>
                )}
                {(searchResults.properties || []).length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-white/40">Properties</div>
                    {/* No property page exists to open, so these stay plain text. */}
                    {searchResults.properties.map((p) => (
                      <div key={p.property_id} className="px-3 py-2 text-[12px] text-white/80">{p.address}</div>
                    ))}
                  </div>
                )}
                {(searchResults.opportunities || []).length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-white/40">Opportunities</div>
                    {searchResults.opportunities.map((o) => (
                      <SearchRow key={o.opportunity_id} {...rowProps(`o:${o.opportunity_id}`)} testId={`gsr-opportunity-${o.opportunity_id}`}>
                        <div className="text-[13px] text-white">{o.name || 'Opportunity'}</div>
                        <div className="text-[11px] text-white/50">{[o.property_street, o.stage].filter(Boolean).join(' · ')}</div>
                      </SearchRow>
                    ))}
                  </div>
                )}
                {(searchResults.transactions || []).length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-white/40">Transactions</div>
                    {searchResults.transactions.map((t) => (
                      <SearchRow key={t.transaction_id} {...rowProps(`t:${t.transaction_id}`)} testId={`gsr-transaction-${t.transaction_id}`}>
                        <div className="text-[12px] text-white/80">{addressLine(t.property_address) || t.buyer_names || t.seller_names}</div>
                      </SearchRow>
                    ))}
                  </div>
                )}
                {(searchResults.loan_files || []).length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-white/40">Loan Files</div>
                    {searchResults.loan_files.map((l) => (
                      <SearchRow key={l.loan_file_id} {...rowProps(`l:${l.loan_file_id}`)} testId={`gsr-loan-${l.loan_file_id}`}>
                        <div className="text-[12px] text-white/80">{l.borrower_name || 'No borrower name'} — {addressLine(l.property_address) || '—'}</div>
                      </SearchRow>
                    ))}
                  </div>
                )}
                {(searchResults.tasks || []).length > 0 && (
                  <div>
                    <div className="px-3 py-1.5 text-[10px] uppercase tracking-wide text-white/40">Tasks</div>
                    {searchResults.tasks.map((t) => (
                      <SearchRow key={t.task_id} {...rowProps(`k:${t.task_id}`)} testId={`gsr-task-${t.task_id}`}>
                        <div className={`text-[13px] ${t.completed ? 'text-white/40 line-through' : 'text-white'}`}>{t.title}</div>
                        {t.due_date && <div className="text-[11px] text-white/50">Due {String(t.due_date).slice(0, 10)}</div>}
                      </SearchRow>
                    ))}
                  </div>
                )}
              </TopNavPanel>
            )}
          </div>

          {/* Right cluster: icons.
              No Tools icon here — added by whichever ticket ships the first real
              tool (Live pricing / Property lookup); see ticket 107. */}
          <div className="flex items-center gap-0.5 shrink-0">
            <IconBtn
              title="Search ⌘K"
              testId="global-search-toggle"
              className="min-[1440px]:hidden"
              onClick={() => { closeMenus(); setSearchExpanded(true); setTimeout(() => searchRef.current?.focus(), 0); }}
            >
              <Search size={16} />
            </IconBtn>
            {/* Tasks + Inbox fall back to the drawer below sm so the cluster fits a phone. */}
            <TasksMenu
              className="hidden sm:block"
              open={openMenu === 'tasks'}
              onOpenChange={(v) => setOpenMenu((cur) => (v ? 'tasks' : cur === 'tasks' ? null : cur))}
            />
            <IconBtn
              title={t('nav.inbox')}
              testId="topnav-inbox"
              to="/portal/inbox"
              className="hidden sm:flex"
              badge={inboxBadge}
            >
              <InboxIcon size={16} />
            </IconBtn>
            <NotificationBell
              open={openMenu === 'bell'}
              onOpenChange={(v) => setOpenMenu((cur) => (v ? 'bell' : cur === 'bell' ? null : cur))}
            />

            {/* Audrey AI (purple gradient) — pill at xl+, round icon-only below xl */}
            <button
              onClick={() => navigate('/portal/ai')}
              data-testid="topnav-ai"
              title={t('nav.ai')}
              className="sm:ml-1 w-8 h-8 xl:w-auto xl:px-3 flex xl:inline-flex items-center justify-center xl:justify-start gap-1.5 rounded-full text-white text-[12px] font-semibold shadow-md hover:opacity-90 transition"
              style={{ background: 'linear-gradient(135deg, #7c3aed 0%, #4f46e5 100%)' }}
            >
              <Sparkles size={13} /> <span className="hidden xl:inline">{t('nav.ai')}</span>
            </button>

            {/* + Add menu */}
            <div className="relative sm:ml-1" ref={addRef}>
              <button
                type="button"
                onClick={() => toggleMenu('add')}
                data-testid="topnav-add"
                className="w-8 h-8 rounded-full flex items-center justify-center hover:opacity-90 transition"
                style={{ background: 'var(--brand-accent, #6BA82E)', color: 'var(--brand-on-accent, #030524)' }}
              >
                <Plus size={16} strokeWidth={2.5} />
              </button>
              {addMenu && (
                <TopNavPanel ref={addPanelRef} anchorRef={addRef} onDismiss={closeMenus} className="w-48 max-w-[calc(100vw-1rem)] rounded-lg border shadow-2xl py-1.5" style={{ background: 'var(--brand-primary, #070B5F)', borderColor: 'color-mix(in srgb, var(--brand-accent, #6BA82E) 25%, transparent)' }}>
                  {hasProduct('mortgage') && (
                    <QuickAddItem to="/portal/loans?new=1" onClose={closeMenus} label={t('add.loan')} />
                  )}
                  <QuickAddItem to="/portal/leads?new=1" onClose={closeMenus} label={t('add.lead')} />
                  <QuickAddItem to="/portal/contacts?new=1" onClose={closeMenus} label={t('add.contact')} />
                  {hasProduct('real_estate') && (
                    <QuickAddItem to="/portal/real-estate" onClose={closeMenus} label={t('add.transaction')} />
                  )}
                  <QuickAddItem to="/portal/tasks?new=1" onClose={closeMenus} label={t('add.task')} />
                </TopNavPanel>
              )}
            </div>

            {/* Avatar */}
            <div className="relative sm:ml-1" ref={userRef}>
              <button
                type="button"
                onClick={() => toggleMenu('user')}
                data-testid="topnav-avatar"
                className="w-8 h-8 rounded-full flex items-center justify-center font-semibold text-xs hover:opacity-90 transition"
                style={{ background: 'var(--brand-highlight, #A6E56C)', color: 'var(--brand-on-highlight, #030524)' }}
              >
                {user.picture && !avatarBroken ? (
                  <img
                    src={user.picture}
                    alt={user.name}
                    onError={() => setAvatarBroken(true)}
                    className="w-8 h-8 rounded-full object-cover"
                  />
                ) : (
                  user.name?.[0]?.toUpperCase() || 'U'
                )}
              </button>
              {userMenu && (
                <TopNavPanel ref={userPanelRef} anchorRef={userRef} onDismiss={closeMenus} className="w-64 max-w-[calc(100vw-1rem)] rounded-lg border shadow-2xl py-1.5" style={{ background: 'var(--brand-primary, #070B5F)', borderColor: 'color-mix(in srgb, var(--brand-accent, #6BA82E) 25%, transparent)' }}>
                  <div className="px-3 py-2 border-b border-white/10">
                    <div className="font-semibold text-sm text-white">{user.name}</div>
                    <div className="text-xs text-white/60 truncate" data-testid="topnav-user-email">{user.email}</div>
                    <div className="text-[10px] uppercase tracking-wider mt-1" style={{ color: '#A6E56C' }} data-testid="topnav-user-role">{user.role}</div>
                  </div>
                  {visibleAdminLinks.length > 0 && (
                    <>
                      <div className="text-[10px] uppercase tracking-wide text-white/40 px-3 pt-2 pb-1">{t('nav.workspace')}</div>
                      {visibleAdminLinks.map((it) => (
                        <NavLink
                          key={it.to}
                          to={it.to}
                          data-testid={it.testId}
                          onClick={closeMenus}
                          className="block px-3 py-2 text-sm text-white/80 hover:bg-white/5 hover:text-white"
                        >
                          {t(it.tk)}
                        </NavLink>
                      ))}
                      <div className="my-1 border-t border-white/10" />
                    </>
                  )}
                  {!imp && (
                    <button
                      type="button"
                      data-testid="topnav-account"
                      onClick={() => { closeMenus(); navigate('/portal/account'); }}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-white/80 hover:bg-white/5 hover:text-white"
                    >
                      <UserCog size={12} /> {t('nav.account')}
                    </button>
                  )}
                  {/* Per-seat bell + email switches (ticket 274). Staff seats only; like Account, hidden while logged in as someone else. */}
                  {!imp && !isExternalRole(user.role) && (
                    <button
                      type="button"
                      data-testid="topnav-my-notifications"
                      onClick={() => { closeMenus(); navigate('/portal/my-notifications'); }}
                      className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-white/80 hover:bg-white/5 hover:text-white"
                    >
                      <Bell size={12} /> My notifications
                    </button>
                  )}
                  <button
                    type="button"
                    data-testid="topnav-feedback"
                    onClick={() => { closeMenus(); setFeedbackOpen(true); }}
                    className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-white/80 hover:bg-white/5 hover:text-white"
                  >
                    <MessageSquare size={12} /> Send feedback
                  </button>
                  <NavLink to="/portal/training" onClick={closeMenus} className="block px-3 py-2 text-sm text-white/80 hover:bg-white/5 hover:text-white">{t('nav.help')}</NavLink>
                  <NavLink to="/" onClick={closeMenus} className="block px-3 py-2 text-sm text-white/80 hover:bg-white/5 hover:text-white">Marketing Site</NavLink>
                  {isOwner && (
                    <NavLink to="/portal/super-admin" data-testid="topnav-super-admin" onClick={closeMenus} className="flex items-center gap-2 px-3 py-2 text-sm hover:bg-white/5" style={{ color: '#FCA5A5' }}>
                      <Shield size={12} /> Super Admin Console
                    </NavLink>
                  )}
                  <div className="my-1 border-t border-white/10" />
                  <button onClick={doLogout} data-testid="topnav-logout" className="w-full text-left flex items-center gap-2 px-3 py-2 text-sm text-white/80 hover:bg-white/5 hover:text-white">
                    <LogOut size={13} /> {t('action.signout')}
                  </button>
                </TopNavPanel>
              )}
            </div>
          </div>
        </div>

        {/* Mobile primary nav drawer — every reachable page: primary nav, Tasks/Inbox
            (icons only on desktop), More (both groups), then admin + Training. */}
        {mobileOpen && <MenuBackdrop onDismiss={closeMenus} />}
        {mobileOpen && (
          <div className="lg:hidden max-h-[calc(100dvh-6rem)] overflow-y-auto overscroll-contain" data-scroll-lock-allow="">
          <WorkspaceSwitcher variant="drawer" />
          <nav className="border-t border-white/10 px-2 py-2 grid grid-cols-2 gap-1" data-testid="topnav-mobile">
            {[
              ...visibleNav,
              { to: '/portal/tasks', label: 'Tasks', tk: 'nav.tasks' },
              { to: '/portal/inbox', label: 'Inbox', tk: 'nav.inbox', badge: inboxBadge },
              { to: '/portal/ai', label: 'Audrey AI', tk: 'nav.ai' },
              ...visibleMore,
              ...visibleAdminLinks,
              { to: '/portal/training', label: 'Help & Training', tk: 'nav.help' },
            ].map((it) => (
              <NavLink
                key={it.to}
                to={it.to}
                end={it.end}
                onClick={closeMenus}
                className={({ isActive }) => `flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm font-medium ${isActive ? 'bg-white/10 text-white' : 'text-white/70 hover:bg-white/5 hover:text-white'}`}
              >
                <span className="truncate">{t(it.tk)}</span>
                {it.badge > 0 && (
                  <span className="min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center shrink-0" style={{ background: 'var(--brand-accent, #6BA82E)', color: 'var(--brand-on-accent, #030524)' }}>{it.badge}</span>
                )}
              </NavLink>
            ))}
          </nav>
          </div>
        )}
      </header>

      <main className="flex-1 min-w-0">
        {/* Ticket 70: a super-admin with no seat anywhere lands on Super Admin and
            enters tenants by impersonation; everyone else gets the holding state. */}
        {noWorkspace && !user.is_super_admin
          ? <NoWorkspace email={user.email} onLogout={doLogout} />
          : noWorkspace && !location.pathname.startsWith('/portal/super-admin')
            ? <Navigate to="/portal/super-admin" replace />
            : <AppErrorBoundary inline resetKey={location.pathname}><Outlet context={{ replayTour: () => setShowTour(true) }} /></AppErrorBoundary>}
      </main>

      {showTour && !noWorkspace && <OnboardingTour onDone={() => setShowTour(false)} />}
      <FeedbackDialog open={feedbackOpen} onOpenChange={setFeedbackOpen} />
    </div>
  );
}

// 404 { detail: { code: 'no_active_tenant' } } from GET /tenants/mine (ticket 55).
const isNoActiveTenant = (err) =>
  err?.response?.status === 404 && err?.response?.data?.detail?.code === 'no_active_tenant';

function NoWorkspace({ email, onLogout }) {
  return (
    <div className="p-4 md:p-8" data-testid="no-workspace">
      <div className="max-w-lg mx-auto mt-10 rounded-2xl border border-slate-200 bg-white p-8 text-center shadow-sm">
        <div className="mx-auto mb-4 flex h-12 w-12 items-center justify-center rounded-full bg-slate-100 text-brand-navy">
          <Building2 size={22} />
        </div>
        <h1 className="font-serif text-2xl font-bold text-brand-navy">No workspace yet</h1>
        <p className="mt-2 text-sm text-slate-500">
          <b>{email}</b> is signed in but isn&apos;t attached to a workspace. Ask your
          company admin for an invitation, then sign in again.
        </p>
        <button
          type="button"
          onClick={onLogout}
          data-testid="no-workspace-logout"
          className="mt-6 inline-flex items-center gap-2 rounded-full border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
        >
          <LogOut size={14} /> Sign out
        </button>
      </div>
    </div>
  );
}

// Every IconBtn takes an `onClick` or a `to` — never neither (ticket 107: no
// no-op controls in the header).
function IconBtn({ children, badge, testId, title, onClick, to, className = '' }) {
  const classes = `relative w-8 h-8 rounded-md flex items-center justify-center text-white/70 hover:text-white hover:bg-white/5 transition ${className}`;
  const badgeEl = typeof badge === 'number' && badge > 0 && (
    <span className="absolute top-1 right-1 min-w-[14px] h-[14px] px-1 rounded-full text-[9px] font-bold flex items-center justify-center" style={{ background: 'var(--brand-accent, #6BA82E)', color: 'var(--brand-on-accent, #030524)' }}>{badge}</span>
  );
  if (to) {
    return (
      <NavLink to={to} data-testid={testId} title={title} aria-label={title} className={classes}>
        {children}
        {badgeEl}
      </NavLink>
    );
  }
  return (
    <button type="button" onClick={onClick} className={classes} data-testid={testId} title={title}>
      {children}
      {badgeEl}
    </button>
  );
}

// One openable row in the global-search results. Opens on mousedown (with
// preventDefault so the input keeps focus), like the Contacts rows always did.
function SearchRow({ id, active, onGo, onHover, testId, children }) {
  return (
    <button
      type="button"
      id={id}
      role="option"
      aria-selected={active}
      onMouseDown={(e) => { e.preventDefault(); onGo(); }}
      onMouseEnter={onHover}
      className={`block w-full text-left px-3 py-2 transition ${active ? 'bg-white/10' : 'hover:bg-white/5'}`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function QuickAddItem({ to, label, onClose }) {
  return (
    <NavLink
      to={to}
      onClick={onClose}
      className="block px-3 py-2 text-[13px] text-white/80 hover:bg-white/5 hover:text-white"
    >
      {label}
    </NavLink>
  );
}
